import { apiService } from './api';

/** Mirrors backend/src/models/Timetable*.ts and services/timetable/*. */

export interface TimetableRow {
  kind: 'period' | 'break';
  label?: string;
  start: string;
  end: string;
}

export interface TimetableGrid {
  days: number[];
  rows: TimetableRow[];
  shortDays: { day: number; periods: number }[];
}

export interface TimetableConfig extends TimetableGrid {
  _id: string;
  sessionId: string;
}

export type PreferredTime = 'any' | 'morning' | 'afternoon';

export interface Lesson {
  key: string;
  /** `<sectionKey>:<subjectId>`: every share of one subject in one section. */
  group: string;
  /** One teacher's share of a subject split between teachers. */
  split: boolean;
  /** The subject's periods and doubles a week in the curriculum (all shares together). */
  groupCount: number;
  groupDoubles: number;
  sectionKey: string;
  classId: string;
  className: string;
  classOrder: number;
  sectionInstanceId: string | null;
  sectionName: string | null;
  subjectId: string;
  subjectName: string;
  teacherId: string | null;
  teacherName: string | null;
  curriculumId: string;
  count: number;
  doubles: number;
  maxPerDay: number | null;
  roomType: string | null;
  preferredTime: PreferredTime;
}

export type ProblemFix = 'school_day' | 'lessons' | 'curriculum' | 'teaching_loads' | 'availability' | 'rooms' | 'board';

export interface Problem {
  severity: 'error' | 'warning';
  code: string;
  title: string;
  detail?: string;
  fix: ProblemFix;
  /** Section keys the problem concerns; absent when it concerns the whole school. */
  sections?: string[];
}

export interface Slot {
  day: number;
  period: number;
}

export type AvailabilityKind = 'teacher' | 'section' | 'room';

export interface AvailabilityRules {
  unavailable: Slot[];
  preferNot: Slot[];
  maxPerDay: number | null;
}

export interface AvailabilityRow extends AvailabilityRules {
  _id: string;
  kind: AvailabilityKind;
  refId: string;
}

export interface RoomRef {
  _id: string;
  name: string;
  type: string;
}

export interface SectionRef {
  _id: string;
  classId: string;
  name: string;
}

export interface Quality {
  sectionGaps: number;
  teacherGaps: number;
  sameDayRepeats: number;
  preferenceMisses: number;
}

export type TimetableStatus = 'draft' | 'published' | 'archived';

export interface Timetable {
  _id: string;
  sessionId: string;
  status: TimetableStatus;
  grid: TimetableGrid;
  note?: string;
  quality?: Quality;
  publishedAt?: string;
  updatedAt: string;
}

export interface Entry {
  _id: string;
  day: number;
  period: number;
  span: 1 | 2;
  classId: string;
  sectionInstanceId: string | null;
  subjectId: string;
  teacherId: string | null;
  roomId: string | null;
  locked: boolean;
}

export type RunMode = 'fill' | 'fresh';
export type RunSpeed = 'quick' | 'thorough';

/** A period outside a run's scope that its result would move. */
export interface RunMove {
  section: string;
  subject: string;
  teacher: string;
  from: Slot;
  to: Slot;
}

export interface RunOptions {
  mode: RunMode;
  speed: RunSpeed;
  /** Section keys to plan; empty = the whole school. */
  scope: string[];
  /** Let other classes shift a little when the scope doesn't fit; the result then waits for review. */
  allowMoves: boolean;
}

export interface Run {
  _id: string;
  timetableId: string;
  mode: RunMode;
  scope: string[];
  allowMoves: boolean;
  timeLimitSeconds: number;
  status: 'running' | 'review' | 'done' | 'failed' | 'discarded';
  moves?: RunMove[];
  stopRequested: boolean;
  progress: { stage: string; total: number; placed: number };
  result?: { placed: number; unplaced: number; quality: Quality; optimal: boolean };
  error?: string;
  createdAt: string;
  finishedAt?: string;
}

export interface BoardSection {
  key: string;
  classId: string;
  sectionInstanceId: string | null;
  label: string;
  order: number;
}

export interface Board {
  timetable: Timetable | null;
  otherExists: boolean;
  config: TimetableConfig | null;
  entries: Entry[];
  lessons: Lesson[];
  sections: BoardSection[];
  subjects: { _id: string; name: string }[];
  teachers: { _id: string; name: string }[];
  rooms: RoomRef[];
  availability: Record<AvailabilityKind, Record<string, AvailabilityRules>>;
  run: Run | null;
  problems: Problem[];
}

export interface LessonsOverview {
  config: TimetableConfig | null;
  lessons: Lesson[];
  rooms: RoomRef[];
  sections: SectionRef[];
  problems: Problem[];
}

export interface HistoryItem {
  _id: string;
  status: TimetableStatus;
  note?: string;
  publishedAt?: string;
  publishedBy?: { _id: string; name: string };
  quality?: Quality;
}

export type LessonRules = Partial<Pick<Lesson, 'doubles' | 'maxPerDay' | 'roomType' | 'preferredTime'>> & { periodsPerWeek?: number };

export type PrintView = 'class' | 'teacher' | 'room';

const data = <T>(res: { data?: T }) => res.data as T;

export const timetableService = {
  // Setup
  getConfig: async (sessionId: string) => data(await apiService.get<TimetableConfig | null>('/timetable/config', { sessionId })),
  saveConfig: async (sessionId: string, grid: TimetableGrid) =>
    data(await apiService.put<TimetableConfig>('/timetable/config', { sessionId, ...grid })),
  getLessons: async (sessionId: string) => data(await apiService.get<LessonsOverview>('/timetable/lessons', { sessionId })),
  updateLesson: async (curriculumId: string, rules: LessonRules) => {
    const { doubles, ...rest } = rules;
    return data(await apiService.patch(`/timetable/lessons/${curriculumId}`, { ...rest, ...(doubles !== undefined && { doublePeriods: doubles }) }));
  },
  getAvailability: async (sessionId: string) => data(await apiService.get<AvailabilityRow[]>('/timetable/availability', { sessionId })),
  saveAvailability: async (sessionId: string, kind: AvailabilityKind, refId: string, rules: AvailabilityRules) =>
    data(await apiService.put<AvailabilityRow>('/timetable/availability', { sessionId, kind, refId, ...rules })),

  // Timetables
  getBoard: async (sessionId: string, status: 'draft' | 'published') =>
    data(await apiService.get<Board>('/timetable/board', { sessionId, status })),
  history: async (sessionId: string, page = 1, limit = 10) =>
    data(await apiService.get<{ items: HistoryItem[]; total: number }>('/timetable/history', { sessionId, page, limit })),
  createDraft: async (sessionId: string, from: 'empty' | 'published' | string, replace = false) =>
    data(await apiService.post<Timetable>('/timetable/drafts', { sessionId, from, replace })),
  discardDraft: async (id: string) => { await apiService.delete(`/timetable/${id}`); },
  publish: async (id: string, note?: string) => data(await apiService.post<Timetable>(`/timetable/${id}/publish`, { note })),
  clearUnlocked: async (id: string) => { await apiService.post(`/timetable/${id}/clear`); },

  // Editing a draft
  place: async (id: string, lessonKey: string, day: number, period: number, span: 1 | 2) =>
    data(await apiService.post<Entry>(`/timetable/${id}/entries`, { lessonKey, day, period, span })),
  move: async (id: string, entryId: string, day: number, period: number) =>
    data(await apiService.patch<Entry>(`/timetable/${id}/entries/${entryId}`, { day, period })),
  swap: async (id: string, a: string, b: string) => data(await apiService.post<Entry[]>(`/timetable/${id}/entries/swap`, { a, b })),
  unplace: async (id: string, entryId: string) => { await apiService.delete(`/timetable/${id}/entries/${entryId}`); },
  lock: async (id: string, entryIds: string[], locked: boolean) => {
    await apiService.post(`/timetable/${id}/entries/lock`, { entryIds, locked });
  },

  // Generation
  startRun: async (id: string, options: RunOptions) => data(await apiService.post<Run>(`/timetable/${id}/runs`, options)),
  getRun: async (runId: string) => data(await apiService.get<Run>(`/timetable/runs/${runId}`)),
  stopRun: async (runId: string) => data(await apiService.post<Run>(`/timetable/runs/${runId}/stop`)),
  applyRun: async (runId: string) => data(await apiService.post<Run>(`/timetable/runs/${runId}/apply`)),
  discardRun: async (runId: string) => data(await apiService.post<Run>(`/timetable/runs/${runId}/discard`)),

  // Printing — the server lays the pages out; the PDF is drawn in the browser.
  getPrint: async (sessionId: string, status: 'draft' | 'published', view: PrintView, ids?: string[]) =>
    data(await apiService.get<TimetablePrint>(
      `/timetable/print?${new URLSearchParams({ sessionId, status, view, ...(ids?.length ? { ids: ids.join(',') } : {}) })}`,
    )),
  getMyPrint: async () => data(await apiService.get<TimetablePrint>('/my-timetable/print')),
  getMine: async () => data(await apiService.get<{ grid: TimetableGrid | null; periods: TeacherPeriod[] }>('/my-timetable')),
};

/** A printed timetable's columns in day order: each period, and a break where the day has one. */
export type TimetablePrintColumn =
  | { kind: 'period'; label: string; start: string; end: string }
  | { kind: 'break'; label: string };

export type TimetablePrintCell =
  /** Its label shows only on days that have the break. */
  | { kind: 'break'; label: string | null }
  /** Past a short day's last period. */
  | { kind: 'off' }
  | { kind: 'free' }
  /** A double period spans the next period column too. */
  | { kind: 'lesson'; title: string; lines: string[]; span: 1 | 2; subjectId: string | null; subject: string | null };

/** Timetable pages laid out for printing — one per class, teacher or room. */
export interface TimetablePrint {
  school: { name: string; logo: string | null };
  view: PrintView;
  status: 'draft' | 'published';
  publishedAt: string | null;
  columns: TimetablePrintColumn[];
  pages: Array<{ title: string; rows: Array<{ day: string; cells: TimetablePrintCell[] }> }>;
}

export interface TeacherPeriod {
  day: number;
  period: number;
  span: 1 | 2;
  start: string;
  end: string;
  subjectId: string;
  subjectName: string;
  classLabel: string;
  roomName: string | null;
}
